import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import "../components/gantt/gantt.css";
import { Header } from "../components/jackstone/Header";
import { LeftRail }    from "../components/gantt/LeftRail";
import { RightRail }   from "../components/gantt/RightRail";
import { drawBufferChart, drawOptChart } from "../components/gantt/ganttCharts";
import { calculate, http, LONG_TIMEOUT_MS } from "@/lib/api";
import { hasOverride, withOverride } from "@/lib/autocorrect";
import {
  OPT_LIMITS, clampHard, DEFAULT_FLOW_MARGIN_PCT, DEFAULT_SAFETY_MARGIN_PCT,
} from "@/lib/limits";
import {
  buildGanttInputs, saveGanttInputs, syncLinkedFieldsToCalc,
  autocorrectLinked, autocorrectLinkedWithNote, ganttLinkedErrors, ganttErrors, buildMergedCalcInputs,
  n, toPayload, ganttModelCode, LINKED_TO_CALC,
  COST_GOAL_ENABLED, COST_WEIGHT_FIELDS, DEFAULT_OPT_OBJECTIVE, OPT_OBJECTIVES,
  type GanttInputs, type FullResult, type OptimizeResult, type OptimizeOption,
  type OptObjective, type OptimizeRequestBody, type CostWeightOverrides,
} from "../components/gantt/ganttTypes";
import { goalUnit, localiseGanttText } from "../components/gantt/ganttUnits";
import { useSettings } from "@/hooks/useSettings";

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

/** Cost-weight overrides → request payload: only the non-empty, finite, ≥ 0
 *  values (as numbers); null when none, so the backend uses its defaults. */
function costWeightPayload(cw: CostWeightOverrides): OptimizeRequestBody["cost_weights"] {
  const out: NonNullable<OptimizeRequestBody["cost_weights"]> = {};
  COST_WEIGHT_FIELDS.forEach(({ key }) => {
    const raw = (cw[key] ?? "").trim();
    if (raw === "") return;
    const v = Number(raw);
    if (isFinite(v) && v >= 0) out[key] = v;
  });
  return Object.keys(out).length ? out : null;
}

/** AUTO/MAN override fields on the Gantt. */
type GanttManualKey = "freezeOverride" | "defrostOverride";
type GanttManualFlags = Record<GanttManualKey, boolean>;
const MAN_EMPTY_MSG = "Enter a value or switch back to AUTO";
const OFFLINE_MSG = "Can't reach the server — retrying on your next edit";

/** True for a network failure / timeout (no HTTP response at all). */
const isOffline = (e: any) => !!e && !e.response && e.code !== "ERR_CANCELED";

export default function GanttPage() {
  const { uiUnits } = useSettings();   // display units only — state / payloads stay SI
  const [inputs, setInputs]   = useState<GanttInputs>(buildGanttInputs);
  const [result, setResult]   = useState<FullResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [optMode, setOptMode] = useState<"crews" | "flow">("flow");
  // Server unreachable on the last simulation (header shows "Offline").
  const [offline, setOffline] = useState(false);
  // AUTO/MAN mode — only the pill changes it, so clearing a MAN field keeps
  // MAN and shows a validation message instead of silently flipping to AUTO.
  const [manual, setManualState] = useState<GanttManualFlags>(() => ({
    freezeOverride: inputs.freezeOverride.trim() !== "",
    defrostOverride: inputs.defrostOverride.trim() !== "",
  }));
  // Autocorrect notes from the last blur, cleared on the next edit of the field.
  const [notes, setNotes] = useState<Partial<Record<keyof GanttInputs, string>>>({});

  // Auto-optimise (goal-seek) state
  const [optimizing, setOptimizing] = useState(false);
  const [optResult, setOptResult]   = useState<OptimizeResult | null>(null);
  const [flowMargin, setFlowMargin] = useState(String(DEFAULT_FLOW_MARGIN_PCT));       // % extra raw flow on top of the minimum
  const [freezeMargin, setFreezeMargin] = useState(String(DEFAULT_SAFETY_MARGIN_PCT)); // % longer freeze time the plant must survive
  const [maxCrews, setMaxCrews]     = useState("4");      // crew search cap, 1..4
  const [costWeights, setCostWeights] = useState<CostWeightOverrides>({}); // "" / missing = backend default
  const [optObjective, setOptObjective] = useState<OptObjective>(DEFAULT_OPT_OBJECTIVE);
  // `${n_freezers}-${n_stations}-${n_crews}` of the optimiser design last applied.
  const [appliedKey, setAppliedKey] = useState<string | null>(null);
  const optAbortRef = useRef<AbortController | null>(null);

  // Live freeze-time / defrost computed by the calc engine (AUTO mode source).
  const [autoFreeze, setAutoFreeze] = useState<number | null>(null);
  const [autoDefrost, setAutoDefrost] = useState<number | null>(null);
  const [autoError, setAutoError] = useState<string | null>(null);
  // Results on screen no longer match the inputs (invalid input, or the
  // re-simulation after an invalid state has not landed yet).
  const [stale, setStale] = useState(false);
  // Stale-response guards (same pattern as useCalculator's reqId): only the
  // latest request may write its result.
  const calcReqId = useRef(0);
  const simReqId  = useRef(0);

  const bufCanvasRef = useRef<HTMLCanvasElement>(null);
  const optCanvasRef = useRef<HTMLCanvasElement>(null);
  const debounceRef  = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const calcDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Always-current inputs for the calc effect (so it can read the latest values
  // without depending on the whole `inputs` object on every keystroke).
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;

  // Persist Gantt-own inputs AND write linked fields back to Calculator
  useEffect(() => {
    saveGanttInputs(inputs);
    syncLinkedFieldsToCalc(inputs);
  }, [inputs]);

  // ── Live freeze-time calculation (AUTO) ─────────────────────────────────────
  // Recompute the freeze time + defrost from the SAME engine the Calculator
  // uses whenever a sizing/product setting changes, so AUTO mode always stays
  // in sync. Skipped while linked inputs are invalid (mirrors the Calculator).
  // Keyed on a string signature so an edit that leaves the errors unchanged
  // (most keystrokes) does not re-trigger the /calculate effect below.
  const linkedErrSig = useMemo(() => JSON.stringify(ganttLinkedErrors(inputs)), [inputs]);
  const hasLinkedErrors = linkedErrSig !== "{}";
  // Every field error (linked + Gantt-own + overrides + shift/overtime rule) —
  // shown in the rail and blocks the simulation below.
  const allErrors = useMemo(() => {
    const errs = ganttErrors(inputs);
    (Object.keys(manual) as GanttManualKey[]).forEach((k) => {
      if (manual[k] && inputs[k].replace(/!/g, "").trim() === "") errs[k] = MAN_EMPTY_MSG;
    });
    return errs;
  }, [inputs, manual]);
  const hasErrors = Object.keys(allErrors).length > 0;

  // Signature of just the fields that affect the freeze-time calc — avoids
  // re-calling the API on unrelated edits (flow, crews, goal…).
  const calcSig = useMemo(
    () => JSON.stringify([
      inputs.nFreezers, inputs.nStations, inputs.lStation, inputs.dStation,
      inputs.tStation, inputs.nDividers, inputs.rhoProduct,
    ]),
    [inputs.nFreezers, inputs.nStations, inputs.lStation, inputs.dStation,
     inputs.tStation, inputs.nDividers, inputs.rhoProduct],
  );

  useEffect(() => {
    clearTimeout(calcDebounceRef.current);
    // The sizing changed: drop any response still in flight and forget the old
    // AUTO values, so the simulation below holds ("AUTO not resolved yet")
    // instead of pairing the new sizing with the old freeze time.
    calcReqId.current += 1;
    setAutoFreeze(null);
    setAutoDefrost(null);
    if (hasLinkedErrors) return;    // don't compute on invalid input
    calcDebounceRef.current = setTimeout(async () => {
      const myId = ++calcReqId.current;
      try {
        const resp = await calculate(buildMergedCalcInputs(inputsRef.current));
        if (myId !== calcReqId.current) return; // superseded by a newer request
        if (resp.ok && resp.result) {
          if (resp.result.estFreezingTimeCalc != null)
            setAutoFreeze(Math.round(resp.result.estFreezingTimeCalc));
          if (resp.result.defrosting != null)
            setAutoDefrost(resp.result.defrosting);
          setAutoError(null);
        } else {
          // No AUTO value for these inputs — don't simulate on the old one
          setAutoFreeze(null);
          setAutoDefrost(null);
          setAutoError(resp.errors?.[0]
            ? `Freeze time (AUTO): ${resp.errors[0]}`
            : "Could not compute the AUTO freeze time — check the Calculator inputs.");
        }
      } catch (e: any) {
        if (myId !== calcReqId.current) return;
        setAutoFreeze(null);
        setAutoDefrost(null);
        setAutoError(`Could not compute the AUTO freeze time: ${e?.message ?? "request failed"}`);
      }
    }, 350);
    return () => clearTimeout(calcDebounceRef.current);
  }, [calcSig, hasLinkedErrors]);

  // Effective freeze / defrost: MAN override if set, otherwise live AUTO value.
  // null until the AUTO value has resolved — never a hard-coded guess.
  const effFreeze = useMemo<number | null>(() => {
    const o = inputs.freezeOverride.replace(/!/g, "").trim();
    if (o !== "" && isFinite(Number(o))) return Number(o);
    return autoFreeze;
  }, [inputs.freezeOverride, autoFreeze]);

  const effDefrost = useMemo<number | null>(() => {
    const o = inputs.defrostOverride.replace(/!/g, "").trim();
    if (o !== "" && isFinite(Number(o))) return Number(o);
    return autoDefrost;
  }, [inputs.defrostOverride, autoDefrost]);
  const timesResolved = effFreeze !== null && effDefrost !== null;

  // ── Simulation API ────────────────────────────────────────────────────────

  const runSimulate = useCallback(async (inp: GanttInputs, freezeMin: number, defrostMin: number) => {
    const myId = ++simReqId.current;
    setLoading(true);
    setError(null);
    try {
      const { data } = await http.post("/batches/full_simulate", toPayload(inp, freezeMin, defrostMin));
      if (myId !== simReqId.current) return; // superseded by a newer request
      setOffline(false);
      if (data.ok) {
        setResult(data as FullResult);
        setStale(false);
      } else {
        setError(data.errors?.[0] ?? "Simulation failed");
        setStale(true);
      }
    } catch (e: any) {
      if (myId !== simReqId.current) return;
      const off = isOffline(e);
      setOffline(off);
      setError(off ? OFFLINE_MSG : (e.message ?? "Request failed"));
      setStale(true);
    } finally {
      if (myId === simReqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (hasErrors) {
      // out-of-range / malformed input — don't simulate on it, drop any
      // in-flight response and grey the results until a valid run lands
      simReqId.current += 1;
      setLoading(false);
      setStale(true);
      return;
    }
    if (effFreeze === null || effDefrost === null) return; // AUTO not resolved yet
    debounceRef.current = setTimeout(() => runSimulate(inputs, effFreeze, effDefrost), 450);
    return () => clearTimeout(debounceRef.current);
  }, [inputs, effFreeze, effDefrost, runSimulate, hasErrors]);

  // ── Canvas redraws ────────────────────────────────────────────────────────

  const redrawCharts = useCallback(() => {
    if (!result) return;
    if (bufCanvasRef.current)
      drawBufferChart(bufCanvasRef.current, result.main, n(inputs.goalT, 0), uiUnits);
    if (optCanvasRef.current)
      drawOptChart(optCanvasRef.current, result.crew_sweep, result.flow_sweep,
        optMode, n(inputs.goalT, 0), n(inputs.mFlow, 1600),
        { currentMassKg: result.main.mass_kg, currentCrews: result.main.n_crews, recCrews: result.rec_crews, units: uiUnits });
  }, [result, optMode, inputs.goalT, inputs.mFlow, uiUnits]);

  useEffect(() => { redrawCharts(); }, [redrawCharts]);

  useEffect(() => {
    const obs = new ResizeObserver(redrawCharts);
    if (bufCanvasRef.current) obs.observe(bufCanvasRef.current);
    if (optCanvasRef.current) obs.observe(optCanvasRef.current);
    return () => obs.disconnect();
  }, [redrawCharts]);

  // ── Input helpers ─────────────────────────────────────────────────────────

  // A "!" soft-range override set on the Calculator is sticky: the Gantt's
  // number inputs show the clean value and an edit keeps the marker. It is
  // cleared with the "!" chip (clearOverride) or on the Calculator.
  // setValue takes the SI string directly (unit-converting inputs call it).
  const setValue = useCallback(
    (key: keyof GanttInputs) =>
      (value: string) => {
        setNotes(prev => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setInputs(prev => ({
          ...prev,
          [key]: LINKED_TO_CALC[key] && hasOverride(String(prev[key] ?? "")) ? withOverride(value) : value,
        }));
      },
    [],
  );
  const set = useCallback(
    (key: keyof GanttInputs) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setValue(key)(e.target.value),
    [setValue],
  );

  // Drop a field's "!" override and apply the normal soft rule.
  const clearOverride = useCallback(
    (key: keyof GanttInputs) =>
      setInputs(prev => ({
        ...prev,
        [key]: autocorrectLinked(key, String(prev[key] ?? "").replace(/!/g, "").trim()),
      })),
    [],
  );

  // Set a field to a raw string value (the MAN input).
  const setVal = useCallback(
    (key: keyof GanttInputs) => (value: string) =>
      setInputs(prev => ({ ...prev, [key]: value })),
    [],
  );

  // AUTO/MAN pill: switch mode, pre-filling the MAN value when switching on.
  const setManual = useCallback((key: GanttManualKey, on: boolean, prefill = "") => {
    setManualState(prev => ({ ...prev, [key]: on }));
    setInputs(prev => ({ ...prev, [key]: on ? prefill : "" }));
  }, []);

  const setCheck = useCallback(
    (key: keyof GanttInputs) =>
      (e: React.ChangeEvent<HTMLInputElement>) =>
        setInputs(prev => ({ ...prev, [key]: e.target.checked })),
    [],
  );

  // On blur, apply the Calculator's autocorrect rule to linked fields so both
  // pages snap/clamp values identically (e.g. length 815 → 820, 75 → 76).
  // A note under the field says what changed ("815 → 820 (standard size)").
  const blur = useCallback(
    (key: keyof GanttInputs) => () => {
      const current = String(inputsRef.current[key] ?? "");
      const { value, note } = autocorrectLinkedWithNote(key, current);
      if (value !== current) setInputs(prev => ({ ...prev, [key]: value }));
      if (note) setNotes(prev => ({ ...prev, [key]: note }));
    },
    [],
  );

  // ── Auto-optimise (goal seek) ──────────────────────────────────────────────

  // Disabled ("coming soon") goals can never become the selected goal.
  const selectObjective = useCallback((key: OptObjective) => {
    if (!OPT_OBJECTIVES.find(o => o.key === key)?.disabled) setOptObjective(key);
  }, []);

  // Put an optimiser design on the live Gantt — only the four allowed levers.
  // Used for the winner after a run and by the trade-off list's Apply buttons.
  const applyOption = useCallback((opt: OptimizeOption | OptimizeResult) => {
    setInputs(prev => ({
      ...prev,
      mFlow:     String(Math.round(opt.m_flow_kg_h)),
      nCrews:    String(opt.n_crews),
      nFreezers: String(opt.n_freezers),
      nStations: String(opt.n_stations),
    }));
    setAppliedKey(`${opt.n_freezers}-${opt.n_stations}-${opt.n_crews}`);
  }, []);

  const runOptimise = useCallback(async () => {
    if (n(inputs.goalT) <= 0) {
      toast.error(`Set a target (${goalUnit(uiUnits)}) before optimising.`);
      return;
    }
    if (hasErrors) {
      toast.error("Fix the highlighted inputs before optimising.");
      return;
    }
    if (effFreeze === null || effDefrost === null) {
      toast.error("The freeze / defrost time is not available yet — check the Calculator inputs.");
      return;
    }
    // abort any in-flight run, then start fresh
    optAbortRef.current?.abort();
    const ctrl = new AbortController();
    optAbortRef.current = ctrl;
    setOptimizing(true);
    setOptResult(null);
    const body: OptimizeRequestBody = {
      params:            toPayload(inputs, effFreeze, effDefrost),
      objective:         optObjective,
      flow_margin_pct:   clampHard(OPT_LIMITS.flowMargin, n(flowMargin, DEFAULT_FLOW_MARGIN_PCT)),
      freeze_margin_pct: clampHard(OPT_LIMITS.freezeMargin, n(freezeMargin, DEFAULT_SAFETY_MARGIN_PCT)),
      max_crews:         clamp(Math.round(n(maxCrews, 4)), 1, 4),
      // Cost weights are only sent while the cost goal is enabled.
      cost_weights:      COST_GOAL_ENABLED ? costWeightPayload(costWeights) : null,
    };
    try {
      const { data } = await http.post("/batches/optimize", body, { signal: ctrl.signal, timeout: LONG_TIMEOUT_MS });
      if (data.ok && data.result) {
        const res = data.result as OptimizeResult;
        setOptResult(res);
        applyOption(res);   // apply the winning design
      } else {
        setError(data.errors?.[0] ?? "Optimise failed");
      }
    } catch (e: any) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        // user cancelled — leave inputs untouched
      } else {
        setError(isOffline(e) ? OFFLINE_MSG : (e?.message ?? "Optimise request failed"));
      }
    } finally {
      if (optAbortRef.current === ctrl) optAbortRef.current = null;
      setOptimizing(false);
    }
  }, [inputs, hasErrors, effFreeze, effDefrost, flowMargin, freezeMargin, maxCrews, costWeights, optObjective, applyOption, uiUnits]);

  const cancelOptimise = useCallback(() => {
    optAbortRef.current?.abort();
    optAbortRef.current = null;
    setOptimizing(false);
    toast("Optimisation cancelled", { description: "Inputs were left unchanged." });
  }, []);

  // Esc cancels the full-screen optimiser overlay (matches modal expectations).
  useEffect(() => {
    if (!optimizing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") cancelOptimise(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [optimizing, cancelOptimise]);

  // ── Derived header values ─────────────────────────────────────────────────

  const modelCode = ganttModelCode(inputs);
  // Charts / results are current only while the inputs are valid and the
  // latest simulation has landed.
  const resultsStale = hasErrors || stale || !timesResolved;
  const nC = parseFloat(inputs.nCrews);

  // A design counts as "Applied" only while the live inputs still hold it — a
  // manual edit of freezers / stations / crews re-enables its Apply button.
  const liveKey = `${Math.round(n(inputs.nFreezers))}-${Math.round(n(inputs.nStations))}-${Math.round(n(inputs.nCrews))}`;
  const liveAppliedKey = appliedKey === liveKey ? appliedKey : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="gantt-app" style={{ background: "var(--gp-page)" }}>
      <Header
        activePage="gantt"
        status={hasErrors ? { tone: "error", label: "Invalid input" }
          : offline && !loading ? { tone: "error", label: "Offline" }
          : loading || !timesResolved ? { tone: "busy", label: "Updating\u2026" }
          : stale ? { tone: "error", label: "Out of date" } : { tone: "ok", label: "Live" }}
        modelCode={modelCode}
        meta={[
          `${inputs.tShift.replace(/!/g, "")} h shift`,
          `${isFinite(nC) ? nC : 1} crew${nC === 1 ? "" : "s"}`,
        ]}
      />

      <div className="gantt-page">
        {error && (
          <div data-testid="gantt-error-banner" style={{
            margin: "12px 20px 0", background: "rgba(179,0,53,.06)",
            border: "1px solid rgba(179,0,53,.35)", borderRadius: 10,
            padding: "10px 14px", display: "flex", gap: 8, alignItems: "center",
            fontSize: 12, color: "#b30035",
          }}>
            <AlertTriangle size={14} /> {localiseGanttText(error, uiUnits)}
          </div>
        )}
        {autoError && !hasLinkedErrors && (
          <div data-testid="gantt-auto-error-banner" role="alert" style={{
            margin: "12px 20px 0", background: "rgba(179,0,53,.06)",
            border: "1px solid rgba(179,0,53,.35)", borderRadius: 10,
            padding: "10px 14px", display: "flex", gap: 8, alignItems: "center",
            fontSize: 12, color: "#b30035",
          }}>
            <AlertTriangle size={14} /> {localiseGanttText(autoError, uiUnits)}
          </div>
        )}
        {resultsStale && result && (
          <div data-testid="gantt-stale-banner" role="status" className="stale-banner">
            <AlertTriangle size={14} /> Results out of date — fix highlighted fields
          </div>
        )}

        <div className={`gantt-layout${resultsStale ? " is-stale" : ""}`}>
          <LeftRail
            inputs={inputs}
            set={set}
            setValue={setValue}
            setVal={setVal}
            setCheck={setCheck}
            blur={blur}
            notes={notes}
            manual={manual}
            setManual={setManual}
            clearOverride={clearOverride}
            errors={allErrors}
            stale={resultsStale}
            result={result}
            autoFreeze={autoFreeze}
            autoDefrost={autoDefrost}
            effFreeze={effFreeze}
            effDefrost={effDefrost}
            flowMargin={flowMargin}
            setFlowMargin={setFlowMargin}
            freezeMargin={freezeMargin}
            setFreezeMargin={setFreezeMargin}
            maxCrews={maxCrews}
            setMaxCrews={setMaxCrews}
            costWeights={costWeights}
            setCostWeights={setCostWeights}
            optObjective={optObjective}
            setOptObjective={selectObjective}
            optimizing={optimizing}
            optResult={optResult}
            appliedKey={liveAppliedKey}
            onApply={applyOption}
            onOptimise={runOptimise}
            onCancel={cancelOptimise}
          />
          <RightRail
            inputs={inputs}
            result={result}
            optMode={optMode}
            setOptMode={setOptMode}
            bufCanvasRef={bufCanvasRef}
            optCanvasRef={optCanvasRef}
          />
        </div>
      </div>
    </div>
  );
}
